import { Flex, Title, Text, Paper, Button } from "@mantine/core";
import { SigningComplete } from "../components/signingComplete";
import { detectBankIdRedirect } from "../utils/detectBankIdRedirect";
import {
  createFileRoute,
  Link,
  getRouteApi,
  useNavigate,
} from "@tanstack/react-router";

import { useUpdateCustomerData } from "../hooks/ui";
import {
  useGetSignStatus,
  useSignOpenPolicy,
  useCreateOpenPolicy,
  useGetTransferPolicy,
} from "../hooks/api";
import capitalizeFirstLetter from "../utils/capitalizeFirstLetter";
import { getAdviceQueryOptions, getCustomerQueryOptions } from "../hooks/api/";
import { useEffect, useRef, useState } from "react";
import { openPolicySigningText } from "../utils/openPolicySigningText";
import {
  getStoredCredentials,
  isFullCredentials,
} from "../hooks/ui/customerDataStore";
import ErrorComponent from "../components/errorComponent";
import { LoaderComponent } from "../components/loaderComponent";
import { FooterButtons } from "../components/footerButtons";
import { BankIdQrCode } from "../components/bankID/qrCode";
import { BankIdPendingMessage } from "../components/login/pendingMessage";
import { TransferStatus } from "../components/transfer/transferStatus";
import { useQueryClient } from "@tanstack/react-query";
import { ContactDetails } from "../components/contactUsPaper/contactDetails";
import { getBankIdFailureMessage } from "../utils/bankIdFailureMessages";
import { RetryErrorCard } from "../components/bankID/retryErrorCard";
import { v4 as uuidV4 } from "uuid";

const initialHash = window.location.hash;

export const Route = createFileRoute("/open-sign-policy")({
  component: RouteComponent,
  loader: async ({ context }) => {
    // If this is a new tab opened by BankID redirect (iOS Chrome), skip data loading, the component will show a "Signering klar!" message
    const hasNonce = window.location.hash.includes("nonce=");
    const credentials = context.credentials?.sessionToken
      ? context.credentials
      : getStoredCredentials();
    if (!credentials?.sessionToken) {
      if (hasNonce) {
        return { customer: null, advice: null };
      }
      throw new Error("Credentials are missing");
    }
    const response = await Promise.all([
      context.queryClient.ensureQueryData(getCustomerQueryOptions(credentials)),
      context.queryClient.ensureQueryData(getAdviceQueryOptions(credentials)),
    ]);
    return {
      customer: response[0],
      advice: response[1],
    };
  },
  pendingComponent: () => <LoaderComponent />,
  errorComponent: () => <ErrorComponent />,
});

function RouteComponent() {
  const { customerData } = useUpdateCustomerData();
  const routeApi = getRouteApi("/open-sign-policy");
  const { customer, advice } = routeApi.useLoaderData();

  // Detect new tab opened by BankID redirect (iOS Chrome), no credentials available
  const [returnedFromBankId] = useState(() =>
    detectBankIdRedirect({
      hash: initialHash,
      nonceKeys: "bankid_open_sign_nonce",
      cleanupKeys: [
        "bankid_open_sign_nonce",
        "bankid_open_sign_transaction_id",
      ],
      condition: !isFullCredentials(customerData.credentials),
    }),
  );

  const [shouldFetchTransferStatus, setShouldFetchTransferStatus] =
    useState(false);
  const [shouldDisplayTransferError, setShouldDisplayTransferError] =
    useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [complete, setComplete] = useState(false);
  const queryClient = useQueryClient();

  const { data: transferredAdvice, isFetching } = useGetTransferPolicy({
    credentials: customerData.credentials,
    enabled:
      !returnedFromBankId &&
      shouldFetchTransferStatus &&
      isFullCredentials(customerData.credentials),
  });

  const [adviceToDisplay, setAdviceToDisplay] = useState(
    advice?.insuranceAdvices,
  );
  const hasCreatedPolicy = useRef(false);
  const hasInitializedSign = useRef(false);
  const nonceRef = useRef<string | null>(null);

  const navigate = useNavigate();

  const createSign = useSignOpenPolicy();
  const createOpenPolicy = useCreateOpenPolicy();
  const getSignStatus = useGetSignStatus({
    id: createSign.data?.body?.id,
    enabled:
      !returnedFromBankId &&
      createSign.isSuccess &&
      !hasCreatedPolicy.current &&
      isFullCredentials(customerData.credentials),
    credentials: customerData.credentials,
  });

  const signProcessStatusInfo =
    getSignStatus.data?.body?.providerInfo?.seBankID?.processStatusInfo;

  const insurances =
    advice?.insuranceAdvices?.map(
      (insurance: { insuranceIdentifier: string }) => {
        return insurance?.insuranceIdentifier;
      },
    ) ?? [];

  useEffect(() => {
    if (returnedFromBankId) return;
    if (hasInitializedSign.current) return;
    hasInitializedSign.current = true;

    const nonce = uuidV4();
    nonceRef.current = nonce;
    localStorage.setItem("bankid_open_sign_nonce", nonce);
    createSign.mutate({
      credentials: customerData.credentials,
      signText: openPolicySigningText({
        firstName: capitalizeFirstLetter(customer[0].user.firstName),
        lastName: capitalizeFirstLetter(customer[0].user.lastName),
        ssn: customer[0].user.ssn,
        insuranceTransferList: advice?.insuranceAdvices,
      }),
      nonce,
      returnPath: window.location.pathname,
    });

    return () => {
      hasInitializedSign.current = false;
    };
  }, []);

  useEffect(() => {
    if (createSign.isSuccess && createSign.data?.body?.id) {
      localStorage.setItem(
        "bankid_open_sign_transaction_id",
        createSign.data.body.id,
      );
    }
  }, [createSign.isSuccess, createSign.data]);

  // On mobile, the tab is fully suspended when BankID app opens.
  // Force a refetch of sign status when the tab becomes visible again.
  const getSignStatusRef = useRef(getSignStatus);
  getSignStatusRef.current = getSignStatus;
  const hasCreatedPolicyRef = useRef(hasCreatedPolicy.current);
  hasCreatedPolicyRef.current = hasCreatedPolicy.current;

  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        !hasCreatedPolicyRef.current
      ) {
        getSignStatusRef.current.refetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (
      shouldFetchTransferStatus &&
      !isFetching &&
      (!transferredAdvice || transferredAdvice.length === 0) &&
      failureCount < 3
    ) {
      const timeoutId = setTimeout(
        () => {
          setFailureCount((count) => count + 1);
          queryClient.invalidateQueries({
            queryKey: ["get-transfer-policy"],
          });
        },
        Math.min(2000 * 2 ** failureCount, 10000),
      ); // 2s, 4s, 8s

      return () => clearTimeout(timeoutId);
    }

    return;
  }, [
    transferredAdvice,
    isFetching,
    shouldFetchTransferStatus,
    failureCount,
    queryClient,
  ]);

  useEffect(() => {
    if (
      getSignStatus.data?.body?.providerInfo?.seBankID?.processStatus ===
      "failed"
    ) {
      hasCreatedPolicy.current = true;
    }

    if (
      getSignStatus.data?.body?.providerInfo?.seBankID?.processStatus ===
        "complete" &&
      !hasCreatedPolicy.current
    ) {
      hasCreatedPolicy.current = true;
      createOpenPolicy.mutate({
        credentials: customerData.credentials,
        insurances: insurances,
        signature: btoa(JSON.stringify(getSignStatus.data?.body)),
      });
    }
  }, [getSignStatus.data?.body?.providerInfo?.seBankID?.processStatus]);

  // If we received error from transfer policy, try to fetch transfer status to cover timeouts
  useEffect(() => {
    if (createOpenPolicy.isError) {
      setShouldFetchTransferStatus(true);
    }
  }, [createOpenPolicy.isError]);

  // Display transferred advice on success or when at least one insurance was processed (at the moment, all or nothing)
  useEffect(() => {
    if (createOpenPolicy.isSuccess || transferredAdvice?.length) {
      setAdviceToDisplay(transferredAdvice);
      navigate({
        to: "/transfer-status",
        replace: true,
      });
    }
  }, [createOpenPolicy.isSuccess, transferredAdvice]);

  // If all retries were used, display error message
  useEffect(() => {
    if (createOpenPolicy.isError && failureCount > 2) {
      setShouldDisplayTransferError(true);
    }
  }, [createOpenPolicy.isError, failureCount]);

  // New tab from BankID redirect (iOS Chrome), show completion message
  if (returnedFromBankId) {
    return (
      <SigningComplete
        description="Signeringen har genomförts. Återgå till din ursprungliga flik där processen slutförs. Du kan stänga den här fliken."
        showCloseButton
      />
    );
  }

  // To seamlessly show transfer status while waiting and right before the redirect
  if (
    !shouldDisplayTransferError &&
    (createOpenPolicy.isPending ||
      createOpenPolicy.isError ||
      isFetching ||
      (transferredAdvice && transferredAdvice.length > 0))
  ) {
    return <TransferStatus isSignInPreparation advice={adviceToDisplay} />;
  }

  if (
    !complete &&
    getSignStatus.data?.body?.status === "started" &&
    (signProcessStatusInfo === "userSign" ||
      signProcessStatusInfo === "started")
  ) {
    return (
      <BankIdPendingMessage
        onCancel={() => setComplete(true)}
        processStatusInfo={signProcessStatusInfo}
      />
    );
  }

  return (
    <>
      <Flex justify="center" align="center" px={"xl"}>
        <Flex direction="column">
          <Title
            order={1}
            size="h2"
            mb="xl"
            mt={"xl"}
            ff={"var(--wallet-font-family-sambla-sans)"}
          >
            Vänligen skriv under med BankID för att öppna nya
            pensionsförsäkringar hos Idun Liv
          </Title>

          <>
            {shouldDisplayTransferError && createOpenPolicy.isError && (
              <Paper p={30} radius={"24px"} mb={"2rem"}>
                <Flex direction={"column"}>
                  <Title order={2} size="h3" mb={"lg"}>
                    Något gick fel
                  </Title>
                  <Text>
                    Det gick inte att signera. Det kan vara för att det tog för
                    långt tid eller för att signeringen avbröts. Kontakta
                    support!
                  </Text>
                </Flex>
                <ContactDetails />
              </Paper>
            )}
            {getSignStatus.data?.body?.providerInfo?.seBankID?.processStatus ===
              "failed" &&
              hasCreatedPolicy.current && (
                <RetryErrorCard
                  title={getBankIdFailureMessage(signProcessStatusInfo).title}
                  description={
                    getBankIdFailureMessage(signProcessStatusInfo).text
                  }
                  onRetry={() => {
                    const nonce = uuidV4();
                    nonceRef.current = nonce;
                    localStorage.setItem("bankid_open_sign_nonce", nonce);
                    createSign.mutate({
                      credentials: customerData.credentials,
                      signText: openPolicySigningText({
                        firstName: customer[0].user.firstName,
                        lastName: customer[0].user.lastName,
                        ssn: customer[0].user.ssn,
                        insuranceTransferList: advice?.insuranceAdvices,
                      }),
                      nonce,
                      returnPath: window.location.pathname,
                    });
                    hasCreatedPolicy.current = false;
                  }}
                  retryLabel="Prova igen"
                />
              )}
            {!hasCreatedPolicy.current && (
              <Paper p={30} radius={"24px"} mb={"2rem"}>
                <BankIdQrCode
                  isLoading={getSignStatus.isLoading || createSign.isPending}
                  qrData={
                    getSignStatus.data?.body?.providerInfo?.seBankID?.qrData
                  }
                  autoStartToken={
                    getSignStatus.data?.body?.providerInfo?.seBankID
                      ?.autoStartToken
                  }
                  error={getSignStatus.isError}
                  instructionText="Starta BankID-appen på din mobila enhet och tryck på 'Skanna QR-kod'. Rikta kameran mot QR-koden och signera."
                  buttonLoading={
                    getSignStatus.isLoading || createSign.isPending
                  }
                  processStatusInfo={signProcessStatusInfo}
                  nonce={nonceRef.current}
                />
              </Paper>
            )}
          </>
        </Flex>
      </Flex>
      <FooterButtons>
        <Button component={Link} to="/transfer-terms" variant="default">
          Tillbaka
        </Button>
      </FooterButtons>
    </>
  );
}
